//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button, TextField } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { savePreferences, saveSearch } from '@/redux/slices/authSlice';
import { selectSavedSearches, selectUserPreferences } from '@/redux/selectors/authSelectors';
import {
  selectCurrentTableKey,
  selectDataTableAppliedSearchName,
  selectDataTableSettings,
  selectSavedSearchUpdated
} from '@/redux/selectors/dataTableSelectors';
import { useToast } from '@/components/_common/Toast';
import Dialog from '@/components/_common/Dialog';
import { PolarisIcon } from '@/exports/columnCommonComponents';

const SaveSearch = () => {
  const isFiltersV2 = useContext(ConfigContext)?.filtersVersion === 2;
  const dispatch = useAppDispatch();
  const savedSearches = useAppSelector(selectSavedSearches);
  const appliedSearchName = useAppSelector(selectDataTableAppliedSearchName);
  const isSearchUpdated = useAppSelector(selectSavedSearchUpdated);
  const { success, error } = useToast();
  const { filters, sorting } = useAppSelector(selectDataTableSettings);
  const history = useHistory();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  // TODO: make this more reusable
  const tableKey = useAppSelector(selectCurrentTableKey);
  const userPrefs = useAppSelector(selectUserPreferences);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setName('');
  };

  const onSubmit = (e: any) => {
    if (e) e.preventDefault();
    const duplicateName = !!savedSearches.filter((ss) => ss.name.toLowerCase() === name.toLowerCase()).length;
    if (!name.trim().length) {
      return error(`Please enter a name to save search.`);
    }
    if (duplicateName) {
      error(`There is already a saved search named "${name}".`);
    } else {
      (dispatch(saveSearch(name)) as any)
        .unwrap()
        .then(() => {
          success(`Saved search "${name}"`);
          const searchParams = new URLSearchParams(location.search);
          searchParams.set('appliedSearchName', name);
          history.push({ ...location, search: searchParams.toString() });
        })
        .catch((err: Error) => {
          console.error(err);
          error(`There was a problem saving "${name}".`);
        })
        .then(handleClose);
    }
  };

  const onUpdateSearch = () => {
    const saved_searches = savedSearches.map((ss) => {
      return ss.name === appliedSearchName ? { ...ss, filters, sorting } : ss;
    });
    dispatch(savePreferences({ [tableKey]: { ...userPrefs[tableKey], saved_searches } }))
      .unwrap()
      .then(() => success(`Updated search "${appliedSearchName}"`))
      .catch((err: Error) => {
        console.error(err);
        error(`There was a problem updating "${appliedSearchName}".`);
      });
  };

  return (
    <div style={{ alignSelf: isFiltersV2 ? 'end' : 'start' }}>
      {!appliedSearchName && (
        <Button
          data-testid="save-search"
          sx={{ height: 30 }}
          size="small"
          onClick={handleOpen}
          startIcon={isFiltersV2 ? null : <PolarisIcon icon="save" />}
        >
          Save {isFiltersV2 ? 'Filter' : 'Search'}
        </Button>
      )}

      {isSearchUpdated && (
        <Button sx={{ height: 30 }} size="small" onClick={onUpdateSearch}>
          Save Changes to &ldquo;{appliedSearchName}&ldquo;
        </Button>
      )}

      <Dialog open={open} onCancel={handleClose} title="Save Search" onSubmit={onSubmit} submitText="Save">
        <StyledForm noValidate autoComplete="off" onSubmit={onSubmit}>
          <TextField
            id="name-search"
            label="Name your search"
            variant="filled"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </StyledForm>
      </Dialog>
    </div>
  );
};

export default SaveSearch;

const StyledForm = styled.form`
  margin-top: ${(props) => props.theme.spacing(2)};
`;
